import { RestfulClient, Session } from '@benzinga/session';
import {
  PostTradeIdea,
  PostTradeIdeaComment,
  TradeIdea,
  Trader,
  TradeIdeaComment,
  ZingFlag,
  TraderStatus,
  TradeIdeaJSON,
  TraderJSON,
  TradeIdeaRequestParams,
  PremiumIdeaRequestParams,
} from './entities';

import { DateTime } from 'luxon';
import { TradeIdeasEnvironment } from './environment';
import { IngressTradeIdeaResponse } from './ingress';
import { SafePromise } from '@benzinga/safe-await';

export class TradeIdeaRestful extends RestfulClient {
  constructor(session: Session) {
    super(session.getEnvironment(TradeIdeasEnvironment).url, session, {
      'x-device-key': true,
    });
  }

  public find = async (queryParams: TradeIdeaRequestParams): SafePromise<IngressTradeIdeaResponse> => {
    const params = new URLSearchParams();

    Object.keys(queryParams).forEach(key => {
      const value = queryParams[key];
      if (!value) return;
      params.append(key, value);
    });

    const url = this.URL(`trade-ideas?${params}`);
    return this.get(url, { allowsAnonymousAuth: true, credentials: 'omit' });
  };

  public getPremiumIdeas = async (queryParams: PremiumIdeaRequestParams, expertProductId: string): SafePromise<any> => {
    const params = new URLSearchParams();

    Object.keys(queryParams).forEach(key => {
      const value = queryParams[key];
      if (!value) return;
      params.append(key, value);
    });

    const hostname = this.session.getEnvironment(TradeIdeasEnvironment).premiumUrl;
    const url = this.URL(`${hostname}trades/product/${expertProductId}?${params}`); //options-nic-chahine
    return this.get(url);
  };

  public getAllPremiumIdeas = async (queryParams: PremiumIdeaRequestParams): SafePromise<any> => {
    const params = new URLSearchParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        const value = queryParams[key];
        if (!value) return;
        params.append(key, value);
      });
    }
    const hostname = this.session.getEnvironment(TradeIdeasEnvironment).premiumUrl;
    const url = this.URL(`${hostname}trades?${params}`);

    return this.get(url);
  };

  public getMorePremiumIdeas = async (
    queryParams: PremiumIdeaRequestParams,
    expertProductId: string,
  ): SafePromise<any> => {
    const params = new URLSearchParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        const value = queryParams[key];
        if (!value) return;
        params.append(key, value);
      });
    }
    const hostname = this.session.getEnvironment(TradeIdeasEnvironment).premiumUrl;
    const url = this.URL(`${hostname}trades/product/${expertProductId}?${params}`);

    return this.get(url);
  };

  public getExpertsArray = async () => {
    const hostname = this.session.getEnvironment(TradeIdeasEnvironment).premiumUrl;
    const url = this.URL(`${hostname}products`);
    return this.get(url);
  };

  public getPackagesArray = async () => {
    const hostname = this.session.getEnvironment(TradeIdeasEnvironment).premiumUrl;
    const url = this.URL(`${hostname}packages`);
    return this.get(url);
  };

  public getTradeIdea = async (tradeIdeaId: string): SafePromise<{ data: TradeIdeaJSON }> => {
    const url = this.URL(`trade-ideas/${tradeIdeaId}`);
    return this.get(url, { allowsAnonymousAuth: true, credentials: 'omit' });
  };

  public getTradersToReview = async (): SafePromise<{ data: TraderJSON[] }> => {
    const t = DateTime.now().toSeconds();
    const url = this.URL(`traders/to-review?t=${t}`);
    return this.get(url, { credentials: 'omit' });
  };

  public reviewTrader = async (trader: Trader, status: TraderStatus): SafePromise<any> => {
    const url = this.URL(`traders/${trader.id}/review`);
    return this.post(
      url,
      {
        status,
      },
      { credentials: 'omit' },
    );
  };

  public postTradeIdea = async (data: PostTradeIdea): SafePromise<{ data: TradeIdeaJSON }> => {
    const url = this.URL(`trade-ideas?limit=30`);
    return await this.post(url, data, { credentials: 'omit' });
  };

  public postTradeIdeaComment = async (data: PostTradeIdeaComment): SafePromise<TradeIdeaComment | any> => {
    const url = this.URL(`trade-idea-comments`);
    return this.post(url, data, { credentials: 'omit' });
  };

  public flag = async (trade_idea_id: number, type: string, reason: string | null = null): SafePromise<ZingFlag> => {
    const url = this.URL(`trade-ideas/${trade_idea_id}/flag`);

    return this.post(
      url,
      {
        reason: reason,
        type: type,
      },
      { credentials: 'omit' },
    );
  };

  public vote = async (tradeIdea: TradeIdea, type: 'upvote' | 'downvote'): SafePromise<any> => {
    const url = this.URL(`trade-ideas/${tradeIdea.id}/vote`);
    return this.post(
      url,
      {
        type,
      },
      { credentials: 'omit' },
    );
  };

  public deleteTradeIdea = async (trade_idea_id: number) => {
    const url = this.URL(`trade-ideas/${trade_idea_id}`);
    return this.delete(url, { credentials: 'omit' });
  };
}
